import type { SVGProps } from "react"
import { t } from "@lingui/core/macro"
import { cn } from "@/lib/utils"

export type ShelfiLogoProps = {
  variant?: "primary" | "motion" | "icon"
  size?: "sm" | "md" | "lg" | "xl"
  animated?: boolean
} & SVGProps<SVGSVGElement>

const sizes = {
  sm: { width: 120, height: 30 },
  md: { width: 200, height: 50 },
  lg: { width: 300, height: 75 },
  xl: { width: 400, height: 100 },
}

export function ShelfiLogo({
  variant = "primary",
  size = "md",
  animated = false,
  className,
  ...props
}: ShelfiLogoProps) {
  const { width, height } = sizes[size]
  const isIcon = variant === "icon"
  const viewBox = isIcon ? "0 0 100 100" : `0 0 ${width} ${height}`

  return (
    <svg
      viewBox={viewBox}
      width={isIcon ? height : width}
      height={height}
      className={cn("shelfi-logo", animated && "shelfi-logo--animated", className)}
      role="img"
      aria-label={t`Shelfi - Shelf Space Optimization`}
      {...props}
    >
      {/* Gradient Definitions */}
      <defs>
        <linearGradient id="shelfGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#4FC3F7" />
          <stop offset="100%" stopColor="#4FC3F7" />
        </linearGradient>
        <linearGradient id="shelfGradientDark" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#4FC3F7" />
          <stop offset="100%" stopColor="#4FC3F7" />
        </linearGradient>
      </defs>

      {/* Icon Variant */}
      {variant === "icon" && (
        <>
          <rect
            x="10"
            y="10"
            width="80"
            height="80"
            rx="12"
            className="fill-[#002B87] dark:fill-[#002B87]"
          />
          <g className="shelf-lines">
            <rect
              x="25"
              y="35"
              width="50"
              height="6"
              rx="3"
              className="fill-[#4FC3F7] dark:fill-white"
            />
            <rect
              x="25"
              y="47"
              width="50"
              height="6"
              rx="3"
              className="fill-[#4FC3F7] dark:fill-white"
            />
            <rect
              x="25"
              y="59"
              width="50"
              height="6"
              rx="3"
              className="fill-[#4FC3F7] dark:fill-white"
            />
          </g>
        </>
      )}

      {/* Motion Variant */}
      {variant === "motion" && (
        <>
          <g className="motion-lines opacity-80">
            <line
              x1={width * 0.05}
              y1={height * 0.6}
              x2={width * 0.1}
              y2={height * 0.4}
              stroke="#00A3E0"
              strokeWidth={height * 0.06}
              strokeLinecap="round"
              className={cn(animated && "animate-slide-in", "dark:stroke-[#4FC3F7]")}
            />
            <line
              x1={width * 0.025}
              y1={height * 0.7}
              x2={width * 0.075}
              y2={height * 0.5}
              stroke="#00A3E0"
              strokeWidth={height * 0.06}
              strokeLinecap="round"
              className={cn(
                animated && "animate-slide-in animation-delay-100",
                "dark:stroke-[#4FC3F7]",
              )}
            />
            <line
              x1={0}
              y1={height * 0.8}
              x2={width * 0.05}
              y2={height * 0.6}
              stroke="#00A3E0"
              strokeWidth={height * 0.06}
              strokeLinecap="round"
              className={cn(
                animated && "animate-slide-in animation-delay-200",
                "dark:stroke-[#4FC3F7]",
              )}
            />
          </g>
          <text
            x={width * 0.125}
            y={height * 0.6}
            fontFamily="Inter, sans-serif"
            fontWeight="900"
            fontStyle="italic"
            fontSize={height * 0.48}
            className="fill-[#002B87] dark:fill-[#002B87]"
            letterSpacing="-0.02em"
          >
            SHELFI
          </text>
        </>
      )}

      {/* Primary Variant */}
      {variant === "primary" && (
        <>
          <text
            x={width * 0.5}
            y={height * 0.45}
            fontFamily="Inter, sans-serif"
            fontWeight="900"
            fontStyle="italic"
            fontSize={height * 0.48}
            className="fill-[#002B87] dark:fill-[#4FC3F7]"
            textAnchor="middle"
            letterSpacing="-0.02em"
          >
            SHELFI
          </text>
          <g className="shelf-lines">
            <rect
              x={width * 0.125}
              y={height * 0.6}
              width={width * 0.75}
              height={height * 0.04}
              rx={height * 0.02}
              fill="url(#shelfGradient)"
              className={cn(
                animated && "animate-shelf-scan",
                "dark:fill-[url(#shelfGradientDark)]",
              )}
            />
            <rect
              x={width * 0.125}
              y={height * 0.72}
              width={width * 0.75}
              height={height * 0.04}
              rx={height * 0.02}
              fill="url(#shelfGradient)"
              className={cn(
                animated && "animate-shelf-scan animation-delay-200",
                "dark:fill-[url(#shelfGradientDark)]",
              )}
            />
            <rect
              x={width * 0.125}
              y={height * 0.84}
              width={width * 0.75}
              height={height * 0.04}
              rx={height * 0.02}
              fill="url(#shelfGradient)"
              className={cn(
                animated && "animate-shelf-scan animation-delay-400",
                "dark:fill-[url(#shelfGradientDark)]",
              )}
            />
          </g>
        </>
      )}

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 0.8;
          }
        }

        @keyframes shelf-scan {
          0%, 100% {
            transform: scaleX(0.8);
            opacity: 0.5;
          }
          50% {
            transform: scaleX(1);
            opacity: 1;
          }
        }

        .animate-slide-in {
          animation: slide-in 0.5s ease-out backwards;
        }

        .animate-shelf-scan {
          animation: shelf-scan 2s ease-in-out infinite;
        }

        .animation-delay-100 {
          animation-delay: 0.1s;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        @media (prefers-reduced-motion: reduce) {
          .shelfi-logo * {
            animation: none !important;
            transition: none !important;
          }
        }

        .shelfi-logo:hover .shelf-lines rect {
          filter: drop-shadow(0 0 8px #00A3E0);
          transition: filter 0.3s ease-out;
        }

        .dark .shelfi-logo:hover .shelf-lines rect {
          filter: drop-shadow(0 0 8px #002B87);
        }
      `}</style>
    </svg>
  )
}

// Simplified icon component for use in buttons, navigation, etc.
export function ShelfiIcon({
  size = 24,
  className,
  ...props
}: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={cn("shelfi-icon", className)}
      fill="none"
      {...props}
    >
      <rect x="2" y="2" width="20" height="20" rx="4" className="fill-current" />
      <rect
        x="6"
        y="8"
        width="12"
        height="1.5"
        rx="0.75"
        className="fill-white dark:fill-[#002B87]"
      />
      <rect
        x="6"
        y="11.25"
        width="12"
        height="1.5"
        rx="0.75"
        className="fill-white dark:fill-[#002B87]"
      />
      <rect
        x="6"
        y="14.5"
        width="12"
        height="1.5"
        rx="0.75"
        className="fill-white dark:fill-[#002B87]"
      />
    </svg>
  )
}
